"use client";

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  cn,
} from "@workspace/frontend-ui";
import { Children, useEffect, useState } from "react";
import useAutoRotate from "../../shared/useAutoRotate";
import BannerNavigation, { type BannerNavItem } from "../BannerNavigation";

type Props = {
  navItems: BannerNavItem[];
  delay: number;
  isMobile: boolean;
  dir?: "ltr" | "rtl";
};

const CarouselBannerView: FC<Props> = ({
  children,
  navItems,
  delay,
  isMobile,
  dir = "ltr",
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const { selected, goTo } = useAutoRotate(api, delay);
  const maxSlides = useMaxSlides(isMobile);

  const slides = Children.toArray(children).slice(0, maxSlides);
  const visibleNavItems = navItems.slice(0, maxSlides);

  const carousel = (
    <Carousel
      setApi={setApi}
      opts={{ direction: dir, loop: true }}
      className="overflow-hidden"
    >
      <CarouselContent className="ml-0">
        {slides.map((slide, index) => (
          <CarouselItem
            key={visibleNavItems[index]?.id ?? index}
            className="w-full basis-full rounded-xl pl-0"
          >
            {slide}
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );

  if (isMobile) {
    return (
      <div data-testid="mobile-carousel-banner" className="-mx-5 px-5">
        {carousel}
        <div className="mt-4 flex justify-center gap-2">
          {slides.map((_, index) => (
            <button
              key={visibleNavItems[index]?.id ?? index}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => goTo(index)}
              className={cn(
                "h-2 w-2 rounded-full bg-muted-foreground/40",
                index === selected && "bg-muted-foreground",
              )}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="grid grid-flow-row 3xl:grid-cols-[calc(100%-16rem)_16rem] 3xl:grid-rows-none"
      data-testid="desktop-carousel-banner"
    >
      {carousel}
      <BannerNavigation
        items={visibleNavItems}
        selected={selected}
        delay={delay}
        onItemSelect={goTo}
      />
    </div>
  );
};

// Legacy parity: wide desktops (>=1500px) and mobile show 5 banners, smaller
// desktops show 4. Measured once after mount (SSR renders the full list).
const useMaxSlides = (isMobile: boolean) => {
  const [maxSlides, setMaxSlides] = useState(5);
  useEffect(() => {
    setMaxSlides(window.innerWidth >= 1500 || isMobile ? 5 : 4);
  }, [isMobile]);
  return maxSlides;
};

export default CarouselBannerView;
